import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import { AuthCard, FormError, errorMessage } from "../components/AuthCard";
import { MIN_PASSWORD } from "./SignupPage";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const { setUser } = useAuth();
  const [state, setState] = useState<"checking" | "ready" | "invalid" | "done">(token ? "checking" : "invalid");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    api
      .checkResetToken(token)
      .then(() => setState("ready"))
      .catch(() => setState("invalid"));
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (password.length < MIN_PASSWORD) {
      setError(`Use at least ${MIN_PASSWORD} characters for your password.`);
      return;
    }
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setUser(await api.resetPassword(token, password));
      setState("done");
    } catch (err) {
      setError(errorMessage(err, "We couldn't update your password. Try again."));
    } finally {
      setBusy(false);
    }
  }

  if (state === "checking") {
    return (
      <AuthCard title="Reset your password">
        <p className="muted">Checking your link…</p>
      </AuthCard>
    );
  }

  if (state === "invalid") {
    return (
      <AuthCard
        title="This link has expired"
        subtitle="Reset links work once and expire after an hour. Ask for a new one and use the latest email."
        footer={<Link to="/login">Back to sign in</Link>}
      >
        <Link to="/forgot-password" className="btn btn-primary btn-block">
          Send a new reset link
        </Link>
      </AuthCard>
    );
  }

  if (state === "done") {
    return (
      <AuthCard title="Password updated" subtitle="You're signed in. Use your new password next time.">
        <Link to="/" className="btn btn-primary btn-block">
          Continue to your courses
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Choose a new password" subtitle="You'll be signed out on your other devices." onSubmit={onSubmit}>
      <label>
        New password
        <input
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          aria-describedby="password-hint"
        />
      </label>
      <span id="password-hint" className="field-hint field-hint-below">
        At least {MIN_PASSWORD} characters.
      </span>
      <label>
        Confirm new password
        <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      </label>
      <FormError message={error} />
      <button className="btn btn-primary btn-block" disabled={busy}>
        {busy ? "Saving…" : "Save new password"}
      </button>
    </AuthCard>
  );
}
